import React, { useState, useEffect } from "react";
import { Modal, Table, Button } from "react-bootstrap";
import { Label, Input } from "reactstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import useFetchClasses from "../../hooks/useFetchClasses";
import useFetchSections from "../../hooks/useFetchSections";
import { ApiUrl } from "../../../ApiUrl"

const SelectStudentModal = ({ show, handleClose, onSelectStudent }) => {
  const { classes, loading, error } = useFetchClasses();
  const classOptions = Array.isArray(classes) ? classes : [];
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [session, setSession] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const [students, setStudents] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState("");

  const {
    sections,
    loading: sectionLoading,
    error: sectionError,
  } = useFetchSections(selectedClass);
  const sectionOptions = Array.isArray(sections) ? sections : [];
  const studentOptions = Array.isArray(students) ? students : [];

  const fetchSessions = async () => {
    setSessionLoading(true);
    try {
      const response = await fetch(`${ApiUrl.apiurl}AcademicYear/GetAllAcademicYear/`);
      const result = await response.json();

      if (result.message === "Success" && Array.isArray(result.data)) {
        const formattedSessions = result.data.map((year) => ({
          id: year.id,
          code: year.sesion_code,
        }));
        setSessions(formattedSessions);
        setSessionError("");
      } else {
        setSessions([]);
        setSessionError("No sessions found.");
      }
    } catch (err) {
      console.error("Session fetch error:", err);
      setSessions([]);
      setSessionError("Failed to load sessions.");
    } finally {
      setSessionLoading(false);
    }
  };

  const fetchStudents = async () => {
    const classId = selectedClass;
    const sectionId = selectedSection;
    const sessionId = session || localStorage.getItem("academicSessionId");

    if (!classId || !sectionId || !sessionId) return;

    setStudentLoading(true);

    try {
      const response = await fetch(
        `${ApiUrl.apiurl}FILTER/GetstudentBasedClassSection?academic_year_id=${sessionId}&classIds=${classId}&sectionIds=${sectionId}`
      );
      const result = await response.json();

      if (Array.isArray(result?.data)) {
        setStudents(result.data);
      } else {
        setStudents([]);
        setStudentError("No students found.");
      }
    } catch (err) {
      console.error("Student fetch error:", err);
      setStudentError("Failed to load students.");
      setStudents([]);
    } finally {
      setStudentLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [selectedClass, selectedSection]);

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleClear = () => {
    setSession("");
    setSelectedClass("");
    setSelectedSection("");
    setStudents([]);
    setStudentError("");
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
  };

  const handleSectionChange = (e) => {
    const sectionId = e.target.value;
    setSelectedSection(sectionId);
  };

  const handleSelectStudent = (student) => {
    onSelectStudent(student);
    handleClose();
  };

  return (
    <Modal show={show} size="xl" aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton onClick={handleClose}></Modal.Header>
      <Modal.Body>
        <div className="container p-1">
          <div className="row">
            <div className="col-12">
              <div className="card p-0">
                <div className="card-body">
                  <p style={{ marginBottom: "0px", textAlign: "center", fontSize: "20px" }}>Student Search</p>
                  <div className="row mb-2">
                    <div className="col-12">
                      <button type="button" className="btn btn-primary me-2" style={{ width: "150px" }} onClick={fetchStudents}>
                        Search
                      </button>
                      <button type="button" className="btn btn-primary me-2" style={{ width: "150px" }} onClick={handleClear}>
                        Clear
                      </button>
                      <button type="button" className="btn btn-danger me-2" style={{ width: "150px" }} onClick={handleClose}>
                        Close
                      </button>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="row">
                    <div className="col-12 col-md-6 col-lg-4 mb-2">
                      <Label htmlFor="session-select" className="form-label">Session </Label>
                      {sessionLoading ? (
                        <p>Loading sessions...</p>
                      ) : sessionError ? (
                        <p>Error loading sessions: {sessionError}</p>
                      ) : (
                        <Input type="select" id="session-select" value={session} onChange={(e) => setSession(e.target.value)}>
                          <option value="">Select Session</option>
                          {sessions.map((sessionItem) => (
                            <option key={sessionItem.id} value={sessionItem.id}>{sessionItem.code}</option>
                          ))}
                        </Input>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6 col-lg-4 mb-2">
                      <Label htmlFor="from-class" className="form-label">From Class</Label>
                      {loading ? (
                        <p>Loading classes...</p>
                      ) : error ? (
                        <p>Error loading classes: {error}</p>
                      ) : (
                        <Input type="select" id="from-class" value={selectedClass} onChange={handleClassChange}>
                          <option value="">Select Class</option>
                          {classOptions.map((classItem) => (
                            <option key={classItem.id} value={classItem.id}>{classItem.classname}</option>
                          ))}
                        </Input>
                      )}
                    </div>
                    <div className="col-12 col-md-6 col-lg-4 mb-2">
                      <Label htmlFor="from-section" className="form-label">From Section</Label>
                      {sectionLoading ? (
                        <p>Loading sections...</p>
                      ) : sectionError ? (
                        <p>Error loading sections: {sectionError}</p>
                      ) : (
                        <Input type="select" id="from-section" value={selectedSection} onChange={handleSectionChange}>
                          <option value="">Select Section</option>
                          {sectionOptions.map((section) => (
                            <option key={section.section_id} value={section.section_id}>{section.sectionname}</option>
                          ))}
                        </Input>
                      )}
                    </div>
                  </div>

                  {/* Table to Display Student Data */}
                  <div className="row mt-2">
                    <div className="table-responsive">
                      <Table className="table table-bordered ">
                        <thead>
                          <tr>
                            <th>Student Name</th>
                            <th>Admission No</th>
                            <th>BarCode</th>
                            <th>Class</th>
                            <th>Section</th>
                            <th>Father Name</th>
                            <th>Select</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentLoading ? (
                            <tr><td colSpan="7">Loading...</td></tr>
                          ) : studentError ? (
                            <tr><td colSpan="7">{studentError}</td></tr>
                          ) : studentOptions.length > 0 ? (
                            studentOptions.map((student) => (
                              <tr key={student.student_id}>
                                <td>{student.student_name}</td>
                                <td>{student.barcode}</td>
                                <td>{student.class_name}</td>
                                <td>{student.section_name}</td>
                                <td>{student.father_name}</td>
                                <td>
                                  <button type="button" className="btn btn-primary btn-sm" onClick={() => handleSelectStudent(student)}>
                                    Select
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="7">No students found</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SelectStudentModal;
